import PluralizedParameter from "./pluralization/PluralizedParameter";
import PluralizationParser from "./pluralization/PluralizationParser";

export default class Translation {
    private key: string;
    private readonly pluralizationParser: PluralizationParser;
    private translationSource!: string;
    private preparedTextForPluralization!: string;
    private pluralizedParameters!: Array<PluralizedParameter>;

    constructor(key: string, translationSource: string) {
        this.key = key;
        this.pluralizationParser = new PluralizationParser();
        this.setText(translationSource);
    }

    public getText(...parameters: any[]): string {
        return parameters.length === 0 ?
            this.getTextWithoutParameters() :
            this.getParametrizedText(parameters);
    }

    public setText(translationSource: string): void {
        this.translationSource = translationSource;
        try {
            this.pluralizedParameters = this.pluralizationParser.parsePluralizedParameters(translationSource);
            this.preparedTextForPluralization = this.pluralizationParser.prepareSourceTextForPluralization(translationSource);
        } catch(error) {
            throw new Error(`Error parsing translation with key '${this.key}': ${error.message}`);
        }
    }

    private getTextWithoutParameters(): string {
        return this.translationSource;
    }

    private getParametrizedText(parameters: any[]): string {
        var parameterizedText =
            parameters[0] instanceof Object ?
                this.fillNamedParameters(parameters[0]) :
                this.fillNumberedParameters(parameters);

        return parameterizedText
    }

    private fillNamedParameters(parameters: {[key: string]: any}): string {
        var parametrizedText = this.preparedTextForPluralization;
        Object.keys(parameters).forEach((key) => {
            parametrizedText = this.addParameterToText(parametrizedText, `{${key}}`, parameters[key]);
        });
        return parametrizedText;
    }

    private fillNumberedParameters(parameters: string[]): string {
        var parametrizedText = this.preparedTextForPluralization;
        parameters.forEach((parameter, index) =>
            parametrizedText = this.addParameterToText(parametrizedText, `{${index}}`, parameter)
        );
        return parametrizedText;
    }

    private addParameterToText(text: string, parameterIdentifier: string, parameterValue: any) {
        text = text.replace(this.createParameterRegExp(parameterIdentifier), parameterValue);
        if(typeof parameterValue === 'number') {
            text = this.addPluralizationToText(parameterIdentifier, text, parameterValue as number);
        }
        return text;
    }

    private createParameterRegExp(parameterIdentifier: string): RegExp {
        return new RegExp(this.escapeRegExp(parameterIdentifier), 'g');
    }

    private escapeRegExp(text: string): string {
        return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }

    private addPluralizationToText(parameterIdentifier: string, translation: string, parameter: number): string {
        const pluralizationIdentifier = this.pluralizationParser.transformParameterIdentifier(parameterIdentifier);
        const pluralizedParameter = this.getPluralizedParameter(pluralizationIdentifier);
        if(pluralizedParameter === undefined) {
            return translation;
        }
        const pluralizedText = pluralizedParameter.getPluralizedText(parameter);
        return translation.replace(pluralizationIdentifier, pluralizedText);
    }

    private getPluralizedParameter(pluralizationIdentifier: string): PluralizedParameter | undefined {
        const foundParameters = this.pluralizedParameters.filter(parameter =>
            parameter.identifier === pluralizationIdentifier
        );
        return foundParameters.length > 0 ? foundParameters[0] : undefined;
    }
}
